import { useCallback, useEffect, useMemo, useState } from "react";
import { Layout, Page, Card, Text, BlockStack, Box } from "@shopify/polaris";
import { useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "configureStore";
import { fetchValidationAttemptCustomers } from "services/customers.service";
import PaginationControls from "components/pagination/PaginationControls";
import DataTable from "components/datatable/Datatable";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import { Column } from "types/Datatable";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

type CustomerAttemptRow = {
  id: string;
  customerId: string;
  attemptCount: number;
  lastAttemptAt: number | null;
  name: string;
  legalName: string;
  companyCode: string;
  vatCode: string;
  email: string;
  displayName: string;
};

export default function ValidationAttemptCustomersContainer() {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const { role } = useSelector((state: RootState) => state.authReducer);
  const isMaster =
    !!role?.isMaster || !!role?.permissionsgroups?.includes("Master");

  const [rows, setRows] = useState<CustomerAttemptRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [total, setTotal] = useState(0);
  const [paginationState, setPaginationState] = useState({
    page: 1,
    pageSize: 20,
  });

  const { page, pageSize } = paginationState;
  const limit = pageSize;
  const skip = (page - 1) * pageSize;

  const runFetch = useCallback(() => {
    if (!companyId || !isMaster) return;
    setLoading(true);
    fetchValidationAttemptCustomers(companyId, { limit, skip })
      .then((data) => {
        const mapped = ((data.rows || []) as Omit<CustomerAttemptRow, "id" | "displayName">[]).map(
          (row) => ({
            ...row,
            id: row.customerId,
            displayName: row.name || row.legalName || row.customerId,
          })
        );
        setRows(mapped);
        setTotal(typeof data.count === "number" ? data.count : 0);
        setError(false);
      })
      .catch(() => {
        setError(true);
        setRows([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [companyId, isMaster, limit, skip]);

  useEffect(() => {
    runFetch();
  }, [runFetch]);

  const columns: Column[] = useMemo(
    () => [
      {
        key: "displayName",
        name: t("name"),
        type: "string",
        mobileView: "topLeft",
      },
      {
        key: "companyCode",
        name: t("company_code", { defaultValue: "Company code" }),
        type: "string",
        mobileView: "topRight",
      },
      {
        key: "vatCode",
        name: t("vat_code", { defaultValue: "VAT code" }),
        type: "string",
      },
      {
        key: "email",
        name: t("email", { defaultValue: "Email" }),
        type: "string",
        mobileView: "bottomLeft",
      },
      {
        key: "attemptCount",
        name: t("validation_attempt_count"),
        type: "number",
        isDisabledTotal: true,
        mobileView: "bottomRight",
      },
      {
        key: "lastAttemptAt",
        name: t("validation_attempt_last"),
        type: "datetime",
      },
    ],
    [t]
  );

  if (!isMaster) {
    return <Navigate to={`/app/${companyId}/settings`} replace />;
  }

  if (!companyId) {
    return null;
  }

  const totalPages = total > 0 ? Math.ceil(total / pageSize) : 0;
  const hasNextPage = total > 0 && page * pageSize < total;
  const hasPreviousPage = page > 1;

  // The settings frame above already names the page, so the Page has no title of its own.
  return (
    <Page fullWidth>
      <Layout>
        <Layout.Section>
          <div style={{ marginTop: 5 }}>
            <Card>
              <BlockStack gap="300">
                <Text as="p" tone="subdued">
                  {t("validation_attempt_customers_help")}
                </Text>
                {loading ? (
                  <DataTableSkeleton />
                ) : error ? (
                  <Box padding="400">
                    <Text as="p" tone="critical">
                      {t("error_fetching_data", {
                        defaultValue: "Failed to load data",
                      })}
                    </Text>
                  </Box>
                ) : rows.length === 0 ? (
                  <Box padding="400">
                    <Text as="p" alignment="center" tone="subdued">
                      {t("validation_attempt_customers_empty")}
                    </Text>
                  </Box>
                ) : (
                  <>
                    <DataTable
                      collection={rows}
                      columns={columns}
                      isTotalsDisabled
                      rowUrl={(item) =>
                        `/app/${companyId}/settings/customer-form-validation-attempts/${item.customerId}`
                      }
                    />
                    {total > 0 && (
                      <div
                        style={{
                          borderTop: "1px solid rgba(0, 0, 0, 0.06)",
                          padding: 16,
                        }}
                      >
                        <PaginationControls
                          page={page}
                          pageSize={pageSize}
                          total={total}
                          totalPages={totalPages}
                          hasNextPage={hasNextPage}
                          hasPreviousPage={hasPreviousPage}
                          onPageChange={(nextPage) => {
                            if (nextPage > 0 && nextPage !== page) {
                              setPaginationState((prev) => ({
                                ...prev,
                                page: nextPage,
                              }));
                            }
                          }}
                          onPageSizeChange={(nextPageSize) => {
                            if (
                              nextPageSize > 0 &&
                              nextPageSize !== pageSize
                            ) {
                              setPaginationState({
                                page: 1,
                                pageSize: nextPageSize,
                              });
                            }
                          }}
                          pageSizeOptions={PAGE_SIZE_OPTIONS}
                          isLoading={false}
                        />
                      </div>
                    )}
                  </>
                )}
              </BlockStack>
            </Card>
          </div>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
